import React from 'react'
import get from 'lodash/get'
import { useRecordContext, sanitizeFieldRestProps } from 'react-admin'
import {
  Link,
  ListItem,
  Typography,
  styled,
  ListItemAvatar,
  Avatar,
  ListItemText,
  ListItemProps,
  ListItemTextProps,
  LinkProps,
} from '@mui/material'
import { isBlob } from '../../utils/validators'
import { imageUrlConcat } from '../../utils/url-concat'
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile'
import { formatBytes } from '../../utils/file-size'
import { FileFieldProps, GenerateFileItemPropsFn, ListItemWithIconProps } from './type'
import isArray from 'lodash/isArray'
import isEmpty from 'lodash/isEmpty'

const Root = styled('div', {
  name: 'RaFileField',
  overridesResolver: (props, styles) => styles.root,
})({
  display: 'inline-block',
})

const ListItemWithIcon: React.FC<ListItemWithIconProps> = ({
  isImage = false,
  pathValue,
  ...props
}) => {
  return (
    <ListItemAvatar {...props}>
      <Avatar>
        {isImage ? (
          <Avatar src={pathValue} />
        ) : (
          <Avatar>
            <InsertDriveFileIcon />
          </Avatar>
        )}
      </Avatar>
    </ListItemAvatar>
  )
}

const ListItemLink: React.FC<
  ListItemProps & LinkProps & ListItemTextProps & ListItemWithIconProps
> = ({ isImage, pathValue, primary, secondary, ...rest }) => {
  return (
    <ListItem component={Link} {...rest}>
      <ListItemWithIcon isImage={isImage} pathValue={pathValue} />
      <ListItemText primary={primary} secondary={secondary} />
    </ListItem>
  )
}

const defaultFieldProps = { title: 'name', targetSourcePath: 'full_path', src: null }

const generateFileItemProps: GenerateFileItemPropsFn = (file, fieldProps = defaultFieldProps) => {
  const { title, targetSourcePath } = { ...defaultFieldProps, ...fieldProps }
  const fileTitleValue = get(file, title) || title
  // checking the file is image or not?
  const mimeType = get(file, 'rawFile.type') || get(file, 'mimetype')
  const isImage = mimeType.search('image') >= 0
  // get filesize
  const fileSize = formatBytes(get(file, 'rawFile.size') || get(file, 'size'))
  // setting path value
  const srcValue = get(file, targetSourcePath) || get(file, defaultFieldProps.targetSourcePath)
  const srcIsBlob = isBlob(srcValue)
  const srcUrl = imageUrlConcat(srcValue)
  const pathValue = srcIsBlob ? srcValue : srcUrl

  return {
    fileTitleValue,
    mimeType,
    fileSize,
    isImage,
    srcValue,
    srcIsBlob,
    srcUrl,
    pathValue,
  }
}

export const FileField: React.FC<FileFieldProps> = (props) => {
  const { className, emptyText, source, title, target, src, download, ping, rel, ...rest } = props
  const [mainSource, targetSourcePath] = source.split('.')
  const record = useRecordContext(props)
  const sourceValue = get(record, mainSource)
  // if the field doesn't exist.
  if (!sourceValue || isEmpty(sourceValue)) {
    return emptyText ? (
      <Typography component='span' variant='body2' className={className}>
        {emptyText}
      </Typography>
    ) : (
      <Root className={className} {...sanitizeFieldRestProps(rest)} />
    )
  }

  // if the field is array file object
  if (isArray(sourceValue)) {
    return (
      <>
        {sourceValue.map((file) => {
          const { fileTitleValue, fileSize, isImage, pathValue } = generateFileItemProps(file, {
            title,
            targetSourcePath: targetSourcePath || mainSource,
          })
          return (
            <ListItemLink
              key={fileTitleValue}
              href={pathValue}
              title={fileTitleValue}
              target={target}
              download={download}
              ping={ping}
              rel={rel}
              primary={fileTitleValue}
              secondary={fileSize}
              isImage={isImage}
              pathValue={pathValue}
            />
          )
        })}
      </>
    )
  }
  const { fileTitleValue, fileSize, isImage, pathValue } = generateFileItemProps(record, {
    title,
    targetSourcePath: src || targetSourcePath || mainSource,
  })

  return (
    <ListItemLink
      href={pathValue}
      title={fileTitleValue}
      target={target}
      download={download}
      ping={ping}
      rel={rel}
      primary={fileTitleValue}
      secondary={fileSize}
      isImage={isImage}
      pathValue={pathValue}
    />
  )
}
